import { getSatelliteJobErrorMessage, JobStatusType } from "@nova/constants"
import {
    activateHotspotOrthoLayerAction,
    activateHotspotPointLayerAction,
} from "features/entity-caching/flights/slice"
import { selectProject } from "features/entity-caching/project/module"
import { useAppDispatch, useAppSelector } from "integrations/redux/hooks"
import store from "integrations/redux/store"
import { activateFlightPath } from "features/map/layers/slice"
import {
    selectActiveOrthophotos,
    toggleActiveOrthophoto,
} from "plugins/Orthomosaic/OrthomosaicSlice"
import { useEffect, useRef } from "react"
import { selectOrthoImagesAndUploadsByFlightId } from "features/entity-caching/images/orthos/selectors"
import { selectInfraredImagesAndUploadsByFlightId } from "features/entity-caching/images/hotspots/selectors"
import { setGlobalNotificationContext } from "user/profile/slice"
import { GlobalNotificationStatus } from "@nova/component-global-notification"

type FlightLiveState = { status: JobStatusType; hasImages: boolean; hasCogOrtho: boolean }

type MonitoredFlight = {
    id: string
    job?: { status: JobStatusType; extraFeatures?: string | null } | null
    cogOrtho?: { fileId?: string | null } | null
}

type FlightJobCallbacks = {
    onJobCompletion: () => void
    onJobLive: () => void
    onPreviewReady: () => void
    onOrthoComplete: () => void
    onOrthoFailed: () => void
}

export const useJobStatusListener = () => {
    const dispatch = useAppDispatch()
    const project = useAppSelector(selectProject)

    // liveScan: QUEUED→COMPLETED turns on layer; LIVE→COMPLETED toasts + layer; LIVE→LIVE (first image) turns on path+layer.
    // rapid:    QUEUED→COMPLETED turns on layer; LIVE→COMPLETED toasts + layer; LIVE→LIVE (first cogOrtho) preview toast.
    useFlightJobMonitor(project.orthoFlights, selectOrthoImagesAndUploadsByFlightId, (flight) => {
        const enableOrthoLayer = () => {
            const state = store.getState()
            if (!selectActiveOrthophotos()(state).includes(flight.id))
                dispatch(toggleActiveOrthophoto(flight.id))
        }
        return {
            onJobCompletion: enableOrthoLayer,
            onJobLive: () => {
                dispatch(activateFlightPath({ flightId: flight.id }))
                enableOrthoLayer()
            },
            onPreviewReady: () => {
                dispatch(
                    setGlobalNotificationContext({
                        message: "Preview ready — full resolution pending",
                        status: GlobalNotificationStatus.default,
                        timeoutInMs: 30000,
                    })
                )
                enableOrthoLayer()
            },
            onOrthoComplete: () =>
                dispatch(
                    setGlobalNotificationContext({
                        message: "Ortho complete",
                        status: GlobalNotificationStatus.success,
                        timeoutInMs: 10000,
                    })
                ),
            onOrthoFailed: () =>
                dispatch(
                    setGlobalNotificationContext({
                        message: "Ortho failed to generate",
                        status: GlobalNotificationStatus.error,
                        timeoutInMs: 10000,
                    })
                ),
        }
    })

    useFlightJobMonitor(
        project.infraredFlights,
        selectInfraredImagesAndUploadsByFlightId,
        (flight) => ({
            onJobCompletion: () => dispatch(activateHotspotPointLayerAction({ id: flight.id })),
            onJobLive: () => {
                dispatch(activateHotspotOrthoLayerAction({ id: flight.id }))
                dispatch(activateHotspotPointLayerAction({ id: flight.id }))
            },
            onPreviewReady: () =>
                dispatch(
                    setGlobalNotificationContext({
                        message: "IR preview ready — full resolution pending",
                        status: GlobalNotificationStatus.default,
                        timeoutInMs: 30000,
                    })
                ),
            onOrthoComplete: () =>
                dispatch(
                    setGlobalNotificationContext({
                        message: "IR ortho complete",
                        status: GlobalNotificationStatus.success,
                        timeoutInMs: 10000,
                    })
                ),
            onOrthoFailed: () =>
                dispatch(
                    setGlobalNotificationContext({
                        message: "IR ortho failed to generate",
                        status: GlobalNotificationStatus.error,
                        timeoutInMs: 10000,
                    })
                ),
        })
    )

    useSatelliteJobStatusListener()
}

export const useSatelliteJobStatusListener = () => {
    const dispatch = useAppDispatch()
    const satelliteFlights = useAppSelector(selectProject).satelliteFlights
    const prevStatusByFlightId = useRef<Record<string, JobStatusType>>({})

    useEffect(() => {
        satelliteFlights.forEach((flight) => {
            const status = flight.job?.status
            const prevStatus = prevStatusByFlightId.current[flight.id]
            if (status !== undefined) prevStatusByFlightId.current[flight.id] = status

            if (status === prevStatus || prevStatus === undefined) return

            if (status === JobStatusType.COMPLETED) {
                dispatch(
                    setGlobalNotificationContext({
                        message: "Satellite mosaic complete",
                        status: GlobalNotificationStatus.success,
                        timeoutInMs: 10000,
                    })
                )
            } else if (status === JobStatusType.FAILED) {
                dispatch(
                    setGlobalNotificationContext({
                        message: getSatelliteJobErrorMessage(flight.job?.error),
                        status: GlobalNotificationStatus.error,
                        timeoutInMs: 10000,
                    })
                )
            }
        })
    }, [satelliteFlights, dispatch])
}

type ImageSelector = (
    flightId: string
) => (state: ReturnType<typeof store.getState>) => unknown[] | null | undefined

const useFlightJobMonitor = <F extends MonitoredFlight>(
    flights: F[],
    selectImages: ImageSelector,
    getCallbacks: (flight: F) => FlightJobCallbacks
) => {
    const liveStateByFlightId = useRef<Record<string, FlightLiveState>>({})

    const liveImageCount = useAppSelector((state) =>
        flights
            .filter((f) => f.job?.status === JobStatusType.LIVE)
            .reduce((total, f) => total + (selectImages(f.id)(state)?.length ?? 0), 0)
    )

    useEffect(() => {
        const state = store.getState()
        flights.forEach((flight) => {
            const hasImages = (selectImages(flight.id)(state)?.length ?? 0) > 0
            const hasCogOrtho = !!flight.cogOrtho?.fileId
            handleJobStatusChange(
                flight,
                hasImages,
                hasCogOrtho,
                liveStateByFlightId.current,
                getCallbacks(flight)
            )
        })
        // getCallbacks is recreated every render; excluding it is intentional — flights and
        // liveImageCount are the real triggers.
        // eslint-disable-next-line react-hooks/exhaustive-deps
    }, [flights, liveImageCount])
}

const handleJobStatusChange = (
    flight: MonitoredFlight,
    hasImages: boolean,
    hasCogOrtho: boolean,
    output: Record<string, FlightLiveState>,
    {
        onJobCompletion,
        onJobLive,
        onPreviewReady,
        onOrthoComplete,
        onOrthoFailed,
    }: FlightJobCallbacks
) => {
    const status = flight.job?.status
    if (status === undefined) return

    const isRapidMode = !(flight.job?.extraFeatures?.includes("liveScan") ?? false)
    const prev = output[flight.id]

    if (isRapidMode) {
        if (status === JobStatusType.LIVE && hasCogOrtho && !prev?.hasCogOrtho) {
            onPreviewReady()
        } else if (prev?.status === JobStatusType.LIVE && status === JobStatusType.COMPLETED) {
            onOrthoComplete()
            onJobCompletion()
        } else if (prev?.status === JobStatusType.LIVE && status === JobStatusType.FAILED) {
            onOrthoFailed()
        } else if (prev?.status === JobStatusType.QUEUED && status === JobStatusType.COMPLETED) {
            onJobCompletion()
        }
    } else {
        // liveScan
        if (prev?.status === JobStatusType.LIVE && status === JobStatusType.COMPLETED) {
            onOrthoComplete()
            onJobCompletion()
        } else if (prev?.status === JobStatusType.LIVE && status === JobStatusType.FAILED) {
            onOrthoFailed()
        } else if (prev?.status === JobStatusType.QUEUED && status === JobStatusType.COMPLETED) {
            onJobCompletion()
        } else if (
            status === JobStatusType.LIVE &&
            prev?.status === JobStatusType.LIVE &&
            !prev.hasImages &&
            hasImages
        ) {
            onJobLive()
        }
    }

    output[flight.id] = { status, hasImages, hasCogOrtho }
}
